import React, { useState } from 'react';

import SortableList from '../../SortableList/SortableList';

import { AddComponentButton } from './AddComponentButton/AddComponentButton';
import { Component } from './Component/Component';

import type { ComponentResponse, ComponentInput } from '../../../types';
import type { FC } from 'react';

import styles from './ComponentsList.module.scss';

type Props = {
  components: ComponentResponse[];
  onComponentAddClick: (
    index: number,
    component: Partial<ComponentResponse>
  ) => void;
  onComponentRemoveClick: (index: number) => void;
  onComponentsReorder: (components: ComponentResponse[]) => Promise<void>;
  onComponentFormSubmit: (
    componentDraft: Partial<
      ComponentInput & {
        id: string;
        pageId: string;
        index: number;
      }
    >,
    index: number
  ) => Promise<void>;
  onComponentFormCancel: (
    index: number,
    componentDraft: Partial<ComponentResponse>
  ) => void;
  isDisabled?: boolean;
};

export const ComponentsList: FC<Props> = ({
  components,
  onComponentAddClick,
  onComponentRemoveClick,
  onComponentsReorder,
  onComponentFormSubmit,
  onComponentFormCancel,
  isDisabled = false,
}) => {
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  async function onReorder(data: ComponentResponse[]) {
    setIsSavingOrder(true);

    await onComponentsReorder(data);

    setIsSavingOrder(false);
  }

  function onComponentEditStart() {
    setIsEditing(true);
  }

  function onComponentEditEnd() {
    setIsEditing(false);
  }

  return components.length > 0 ? (
    <SortableList
      items={components}
      renderItem={(item: ComponentResponse, index: number) => (
        <Component
          component={item}
          index={index}
          onAddClick={onComponentAddClick}
          onRemoveClick={onComponentRemoveClick}
          onFormSubmit={(data) => onComponentFormSubmit(data, index)}
          onFormCancel={onComponentFormCancel}
          onEditStart={onComponentEditStart}
          onEditEnd={onComponentEditEnd}
          isEditable={!isEditing}
        />
      )}
      onChange={onReorder}
      keyProperty="id"
      disabled={isSavingOrder}
      itemsDisabled={isEditing}
    />
  ) : (
    <div className={styles.emptyList}>
      <AddComponentButton
        index={0}
        label="Add the first component"
        onClick={onComponentAddClick}
        size="big"
        className={styles.addButton}
        disabled={isDisabled}
      />
    </div>
  );
};
